/**
 * RestoID — connector metadata readers shared by the social pages.
 * ---------------------------------------------------------------------------
 * `custom/pages/SocialLanding.tsx` and `custom/pages/SocialLinkAccount.tsx` both
 * render a connector's disc and name from the connector METADATA
 * (`useConnectors().findConnectorById`), exactly as upstream's containers do, so
 * no vendor color is restated in either page. These two readers were identical
 * copies in both pages; they live here so there is one of each.
 */
import { isLanguageTag } from '@logto/language-kit';

import type useConnectors from '@/hooks/use-connectors';

/** The shape `useConnectors().findConnectorById` resolves to (upstream keeps the type private). */
export type ConnectorResult = NonNullable<
  ReturnType<ReturnType<typeof useConnectors>['findConnectorById']>
>;

/**
 * The connector's own display name in the active language — the same lookup the
 * upstream `SocialLinkButton` does (`name[language] ?? name.en`), plus the SSO
 * branch, whose metadata carries a single non-localized `connectorName`.
 */
export const getConnectorName = (result: ConnectorResult, language: string): string => {
  if (result.type === 'sso') {
    return result.connector.connectorName;
  }

  const { name } = result.connector;

  return (isLanguageTag(language) ? name[language] : undefined) ?? name.en;
};

/**
 * The connector's logo for the CURRENT RESTOID THEME, reading the same two metadata
 * fields upstream reads (`darkLogo` for SSO, `logoDark` for social).
 *
 * It exists because `useConnectors().getConnectorLogo` keys the variant off
 * `PageContext.theme`, which never tracks RestoID's: that theme only ever leaves
 * `Theme.Light` when the tenant has Logto's own dark mode enabled, and then follows
 * the OS alone. RestoID is dark BY DEFAULT and selects its theme from
 * `html[data-rid-theme]` — a stored preference the header switch writes — so the two
 * diverge both when the tenant has dark mode off and whenever the switch is set
 * against the OS. Keying off `useRidTheme()` is what stops a dark-ink mark being
 * drawn on the near-black page (or inside the disc's near-black field), or a white
 * one on the light ground.
 */
export const getConnectorLogo = (result: ConnectorResult, isDark: boolean): string => {
  if (result.type === 'sso') {
    return isDark ? (result.connector.darkLogo ?? result.connector.logo) : result.connector.logo;
  }

  return isDark ? (result.connector.logoDark ?? result.connector.logo) : result.connector.logo;
};
